import React, { useCallback, useEffect, useRef, useState } from "react";
import { ukDayYear as fmtDate } from "@/lib/time";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { FileStack, UploadCloud, Loader2, Download, Trash2, Eye, EyeOff } from "lucide-react";

const prettySize = (n) => { n = Number(n) || 0; return n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`; };

/** Say what actually went wrong — "Upload failed" tells nobody anything. */
function uploadError(e) {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  const status = e?.response?.status;
  if (status === 413) return "That file was too big to send through — try a compressed PDF under 75MB";
  if (status === 503) return "File storage isn't responding right now — try again in a minute";
  if (!e?.response) return "The upload didn't reach us — check your connection and try again";
  return `Upload failed (${status}). Try again, or send the file to the desk directly.`;
}

/**
 * PartnerDocsPanel — the counterparty's own paperwork on file. Their panel agreement,
 * their NDA, their terms of business. Filed here, visible in their portal alongside ours.
 */
export function PartnerDocsPanel({ kind, partyId, partyName }) {
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ title: "", doc_type: "Their contract", signed_on: "", notes: "", visible: true });
  const [busy, setBusy] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (!partyId) return;
    try {
      const r = await api.get(`/hub/partner-docs/${kind}/${partyId}`);
      setItems(r.data.items || []); setTypes(r.data.doc_types || []);
    } catch { setItems([]); }
  }, [kind, partyId]);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file) return;
    if (file.size > 75 * 1024 * 1024) {
      toast.error(`That file is ${(file.size / 1048576).toFixed(1)}MB — the limit is 75MB. Try a compressed PDF.`);
      return;
    }
    setBusy("upload");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", form.title);
      fd.append("doc_type", form.doc_type);
      fd.append("signed_on", form.signed_on);
      fd.append("notes", form.notes);
      fd.append("visible_to_partner", form.visible ? "true" : "false");
      await api.post(`/hub/partner-docs/${kind}/${partyId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(form.visible ? "Filed — and it's in their portal now" : "Filed as internal only");
      setFile(null); setForm({ title: "", doc_type: "Their contract", signed_on: "", notes: "", visible: true });
      load();
    } catch (e) { toast.error(uploadError(e)); }
    finally { setBusy(null); }
  };

  const download = async (d) => {
    setBusy(d.id);
    try {
      const r = await api.get(`/hub/partner-doc/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: d.content_type }));
      const a = document.createElement("a"); a.href = url; a.download = d.filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Download failed"); }
    finally { setBusy(null); }
  };

  const toggleVisible = async (d) => {
    setBusy(d.id);
    try {
      const fd = new FormData();
      fd.append("visible_to_partner", d.visible_to_partner ? "false" : "true");
      await api.patch(`/hub/partner-doc/${d.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(d.visible_to_partner ? "Hidden from their portal" : "Now visible in their portal");
      load();
    } catch { toast.error("Could not change that"); }
    finally { setBusy(null); }
  };

  const remove = async (d) => {
    setBusy(d.id);
    try { await api.delete(`/hub/partner-doc/${d.id}`); toast.success("Removed"); load(); }
    catch { toast.error("Could not remove that"); }
    finally { setBusy(null); }
  };

  return (
    <div className="min-w-0 rounded-2xl border border-slate-200 bg-slate-50/60 p-4 space-y-3" data-testid="partner-docs-panel">
      <div className="flex items-center gap-2">
        <FileStack className="size-4 text-slate-500" />
        <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-slate-600">
          Their own paperwork {items.length ? `· ${items.length}` : ""}
        </div>
      </div>
      <p className="text-[11.5px] text-slate-500 -mt-1">
        Anything {partyName || "they"} sent us to sign — their panel agreement, their NDA, their terms of business.
        Filed here and shown in their portal next to ours.
      </p>

      {items.length > 0 && (
        <div className="space-y-1.5">
          {items.map((d) => (
            <div key={d.id} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`partner-doc-${d.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold text-slate-900 truncate">{d.title}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {d.doc_type} · {prettySize(d.size)}
                    {d.signed_on ? ` · signed ${d.signed_on}` : ""} · filed {fmtDate(d.uploaded_at)}
                  </div>
                  {d.notes && <div className="mt-1 text-[11.5px] text-slate-600 italic">{d.notes}</div>}
                </div>
                <div className="shrink-0 inline-flex items-center gap-0.5">
                  <button onClick={() => toggleVisible(d)} disabled={busy === d.id}
                          className="p-1.5 rounded hover:bg-slate-100 text-slate-500"
                          title={d.visible_to_partner ? "Visible in their portal — click to hide" : "Internal only — click to share"}
                          data-testid={`partner-doc-visible-${d.id}`}>
                    {d.visible_to_partner ? <Eye className="size-3.5 text-teal-600" /> : <EyeOff className="size-3.5" />}
                  </button>
                  <button onClick={() => download(d)} disabled={busy === d.id}
                          className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title="Download"
                          data-testid={`partner-doc-download-${d.id}`}>
                    {busy === d.id ? <Loader2 className="size-3.5 animate-spin" /> : <Download className="size-3.5" />}
                  </button>
                  <button onClick={() => remove(d)} disabled={busy === d.id}
                          className="p-1.5 rounded hover:bg-red-50 text-red-500" title="Remove"
                          data-testid={`partner-doc-delete-${d.id}`}>
                    <Trash2 className="size-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div onClick={() => fileRef.current?.click()}
           className="rounded-xl border-2 border-dashed border-slate-300 bg-white p-4 text-center cursor-pointer hover:border-[#0055FF]/40 transition-colors"
           data-testid="partner-doc-drop">
        <UploadCloud className="size-5 text-slate-400 mx-auto" />
        <div className="mt-1.5 text-[12.5px] font-semibold text-slate-800 truncate" title={file ? file.name : undefined}>
          {file ? file.name : "Upload their contract (PDF, JPG, PNG, DOC)"}
        </div>
        <div className="mt-0.5 text-[11px] text-slate-400">
          {file ? prettySize(file.size) : "Up to 75MB — a full scanned bundle is fine"}
        </div>
        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" className="hidden"
               onChange={(e) => { const f = e.target.files?.[0] || null; setFile(f); if (f && !form.title) setForm((s) => ({ ...s, title: f.name.replace(/\.[^.]+$/, "") })); e.target.value = ""; }}
               data-testid="partner-doc-file" />
      </div>

      {file && (
        <div className="space-y-2.5">
          <Input value={form.title} onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
                 placeholder="What is it? e.g. Lender panel agreement 2026" data-testid="partner-doc-title" />
          <div className="grid grid-cols-2 gap-2">
            <Select value={form.doc_type} onValueChange={(v) => setForm((s) => ({ ...s, doc_type: v }))}>
              <SelectTrigger data-testid="partner-doc-type"><SelectValue /></SelectTrigger>
              <SelectContent>{(types.length ? types : ["Their contract"]).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="date" value={form.signed_on} onChange={(e) => setForm((s) => ({ ...s, signed_on: e.target.value }))}
                   data-testid="partner-doc-signed-on" />
          </div>
          <Input value={form.notes} onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))}
                 placeholder="Note for the file (optional)" data-testid="partner-doc-notes" />
          <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3.5 h-11">
            <div className="text-[12.5px] text-slate-700">Show it in their portal</div>
            <Switch checked={form.visible} onCheckedChange={(v) => setForm((s) => ({ ...s, visible: v }))}
                    data-testid="partner-doc-visible-toggle" />
          </div>
          <Button onClick={upload} disabled={busy === "upload"} className="w-full rounded-xl bg-slate-900 hover:bg-slate-800"
                  data-testid="partner-doc-submit">
            {busy === "upload" ? <Loader2 className="size-4 animate-spin" /> : "File this document"}
          </Button>
        </div>
      )}
    </div>
  );
}
